import { useState } from 'react';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { DISPLAY_TIME_ZONE } from './checklistWords';
import { useFamilyStripePaymentMethodsQuery } from './useFamilyStripePaymentMethodsQuery';
import { useRecordPaperSignature } from './useRecordPaperSignature';

export interface PaperSignatureItem {
  id: string;
  title: string;
  stepType?: string | null;
}

interface PaperSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PaperSignatureItem;
  /** The agreement the office was shown, so a replaced offer is refused. */
  agreementId?: string | null;
  familyEnrollmentId: string;
  onSaved?: () => void;
}

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: DISPLAY_TIME_ZONE });

// The family's saved methods couldn't be asked for: not "none saved".
const UNREACHABLE = "We couldn't reach the payment processor. Please try again in a minute.";

// The office records what the family signed on paper: the name as signed and
// the day, and for auto-pay which of the family's saved methods it is for.
// The server's answer decides: a refusal stays in the dialog.
export function PaperSignatureDialog({ open, onOpenChange, item, agreementId = null, familyEnrollmentId, onSaved }: PaperSignatureDialogProps) {
  const autoPay = item.stepType === 'auto_pay';
  const title = autoPay ? 'Record paper authorization' : 'Record paper signature';
  const [name, setName] = useState('');
  const [signedOn, setSignedOn] = useState(today);
  const [methodId, setMethodId] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [record, { loading }] = useRecordPaperSignature();
  const { data: methodsData, error: methodsError } = useFamilyStripePaymentMethodsQuery(familyEnrollmentId, { skip: !autoPay || !open });
  const methods = methodsData?.familyStripePaymentMethods ?? [];
  const unreachable = autoPay && !!methodsError;

  const close = (next: boolean) => {
    if (!next) setErrors([]);
    onOpenChange(next);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setErrors(['Type the name as signed']);
      return;
    }
    const paymentMethodId = autoPay ? methodId || methods.find((m) => m.isDefault)?.paymentMethodId || methods[0]?.paymentMethodId : null;
    if (autoPay && !paymentMethodId) {
      setErrors(['Choose the payment method it is for']);
      return;
    }
    try {
      const result = await record({
        variables: {
          input: {
            itemId: item.id,
            agreementId: autoPay ? null : agreementId,
            signedName: name.trim(),
            signedOn: signedOn || null,
            paymentMethodId: paymentMethodId ?? null,
          },
        },
      });
      const serverErrors = result.data?.recordPaperSignature?.errors ?? [];
      if (serverErrors.length > 0) {
        setErrors([...serverErrors]);
        return;
      }
      setName('');
      setErrors([]);
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'It was not saved. Please try again.']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{item.title}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div role="alert" className="space-y-1 text-sm text-destructive">
              {errors.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor={`paper-name-${item.id}`}>Name as signed</Label>
            <Input id={`paper-name-${item.id}`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`paper-date-${item.id}`}>Date signed</Label>
            <Input id={`paper-date-${item.id}`} type="date" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
          </div>
          {autoPay && (
            <div className="space-y-1">
              <Label htmlFor={`paper-method-${item.id}`}>Payment method</Label>
              <select
                id={`paper-method-${item.id}`}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={methodId || methods.find((m) => m.isDefault)?.paymentMethodId || methods[0]?.paymentMethodId || ''}
                onChange={(e) => setMethodId(e.target.value)}
              >
                {methods.map((method) => (
                  <option key={method.paymentMethodId} value={method.paymentMethodId}>
                    {`${method.brand ?? method.paymentType} ••${method.last4}`}
                  </option>
                ))}
              </select>
              {unreachable ? (
                <p role="alert" className="text-sm text-destructive">{UNREACHABLE}</p>
              ) : methods.length === 0 ? (
                <p className="text-sm text-muted-foreground">The family has no saved payment method yet.</p>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || unreachable}>
              {title}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PaperSignatureDialog;
